"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { stemSplitterSessionStorage } from "@/services/storage/stem-splitter-session-storage";
import { StemSplitterCategory } from "@/data/stem-splitter-categories.constants";

export type StemSplitterSettings = {
  selectedCategories: StemSplitterCategory[];
};

type StemSplitterContextState = {
  settings: StemSplitterSettings;
  toggleCategory: (category: StemSplitterCategory) => void;
  resetCategories: () => void;
};

const StemSplitterContext = createContext<StemSplitterContextState | undefined>(
  undefined,
);

export const useStemSplitterContext = () => {
  if (!StemSplitterContext) {
    throw new Error("StemSplitterContext is undefined");
  }

  return useContext(StemSplitterContext)!;
};

export const initialSelectedCategories = [
  "guitar",
  "piano",
  "drums",
  "bass",
  "vocals",
  "other",
] as StemSplitterCategory[];

const StemSplitterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const initialSettings: StemSplitterSettings = {
    selectedCategories: initialSelectedCategories,
  };

  const [settings, setSettings] =
    useState<StemSplitterSettings>(initialSettings);

  useEffect(() => {
    stemSplitterSessionStorage.setDataFromStorage(setSettings);
  }, []);

  const toggleCategory = async (category: StemSplitterCategory) => {
    setSettings((prevSettings) => {
      const isCategorySelected =
        prevSettings.selectedCategories.includes(category);

      const updatedCategories = isCategorySelected
        ? prevSettings.selectedCategories.filter((item) => item !== category)
        : [...prevSettings.selectedCategories, category];

      return {
        ...prevSettings,
        selectedCategories: updatedCategories,
      };
    });
  };

  const resetCategories = () => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      selectedCategories: [],
    }));
  };

  useEffect(() => {
    stemSplitterSessionStorage.settingStorage.setSettings(
      settings.selectedCategories,
      "category",
    );
  }, [settings.selectedCategories]);

  return (
    <StemSplitterContext.Provider
      value={{
        settings,
        toggleCategory,
        resetCategories,
      }}
    >
      {children}
    </StemSplitterContext.Provider>
  );
};

export default StemSplitterProvider;
